import {
  DELIVERY_SORT_ORDER,
  DELIVERY_SORT_ORDER_CBE,
  IN_SEPARATION_ORDERS,
  NEW_ORDERS,
  ON_GOING_ORDERS,
  ORDERS_COLUMNS_LABELS,
  OrdersColumnsLabels,
} from '@/pages/Orders/constants'
import { ColumnSortingInverted, OrdersByColumn } from '@/pages/Orders/types'
import { getScheduledOrdersKanban } from '@/pages/ScheduledOrders/utils'
import { DeliveryType, OrderStatus } from '@/resources/enums'
import { ActiveOrder } from '@/resources/types'
import { isCbe } from '@/resources/utils/order'

export const getSortedOrders = (
  orders: ActiveOrder[],
  isColumnSortingInverted: ColumnSortingInverted,
  isPriorityOn: boolean
) => {
  const ordersByColumn: OrdersByColumn = getInitialOrderColumns()

  const ordersNotScheduled = orders?.filter(
    ({ delivery, checkoutType }) =>
      delivery.type !== DeliveryType.SCHEDULED && !isCbe(checkoutType)
  )
  const sortedScheduledOrders = getScheduledOrdersKanban({
    orders,
    includeChoppOrders: true,
    includeNewOrders: true,
  })
  handlePriorityOrders(ordersNotScheduled, isPriorityOn)

  collectByOrderByStatus(
    [...ordersNotScheduled, ...sortedScheduledOrders],
    ordersByColumn
  )

  Object.values(ORDERS_COLUMNS_LABELS).forEach(column => {
    ordersByColumn[column].sort(sortByDeliveryTypeAndTime)
    if (isColumnSortingInverted[column]) ordersByColumn[column].reverse()
  })

  return ordersByColumn
}

const getInitialOrderColumns = (): OrdersByColumn => ({
  [OrdersColumnsLabels.NEW_ORDERS]: [],
  [OrdersColumnsLabels.IN_SEPARATION_ORDERS]: [],
  [OrdersColumnsLabels.ON_GOING_ORDERS]: [],
})

const collectByOrderByStatus = (
  orders: ActiveOrder[],
  ordersByColumn: OrdersByColumn
) => {
  orders.forEach(order => {
    switch (order.status) {
      case OrderStatus.PENDING:
      case OrderStatus.TRANSFERED:
        ordersByColumn[NEW_ORDERS].push(order)
        break
      case OrderStatus.POC_ACCEPTED:
      case OrderStatus.DM_ACCEPTED:
        ordersByColumn[IN_SEPARATION_ORDERS].push(order)
        break
      case OrderStatus.DM_PICKED_UP:
      case OrderStatus.DM_EN_ROUTE:
        ordersByColumn[ON_GOING_ORDERS].push(order)
        break
      default:
        break
    }
  })
}

const handlePriorityOrders = (orders: ActiveOrder[], isPriorityOn: boolean) => {
  if (!isPriorityOn) return
  orders.forEach(order => {
    if (
      order.delivery.type === DeliveryType.RECEIVE &&
      order.customer.isFirstPurchase
    ) {
      order.delivery.type = DeliveryType.TURBO
    }
  })
}

const sortByDeliveryTypeAndTime = (
  orderA: ActiveOrder,
  orderB: ActiveOrder
) => {
  const isCbeOrder = isCbe(orderA.checkoutType)
  const sortOrder = isCbeOrder ? DELIVERY_SORT_ORDER_CBE : DELIVERY_SORT_ORDER

  const typeComparison =
    sortOrder[getDeliveryType(orderA)] - sortOrder[getDeliveryType(orderB)]

  if (typeComparison !== 0) return typeComparison
  if (isScheduledOrder(orderA)) return 0

  if (isCbeOrder && isReceiveOrPickUpOrder(orderA))
    return sortByDeliveryDate(orderA, orderB)

  if (isPendingOrder(orderA)) {
    return orderA.expiringTime - orderB.expiringTime
  }
  return orderB.elapsedTime - orderA.elapsedTime
}

const sortByDeliveryDate = (orderA: ActiveOrder, orderB: ActiveOrder) => {
  const startDateA = orderA.delivery.deliveryDate?.startDate
  const startDateB = orderB.delivery.deliveryDate?.startDate

  if (!startDateA && !startDateB) return 0
  if (!startDateA) return 1
  if (!startDateB) return -1

  return new Date(startDateA).getTime() - new Date(startDateB).getTime()
}

const getDeliveryType = (order: ActiveOrder) => {
  const hasChopp = isCbe(order.checkoutType)
  return hasChopp && order.delivery.type !== DeliveryType.PICKUP
    ? DeliveryType.SCHEDULED
    : order.delivery.type
}

const isPendingOrder = (order: ActiveOrder) =>
  order.status === OrderStatus.PENDING ||
  order.status === OrderStatus.TRANSFERED

const isScheduledOrder = (order: ActiveOrder) =>
  order.delivery.type === DeliveryType.SCHEDULED

const isReceiveOrPickUpOrder = (order: ActiveOrder) =>
  order.delivery.type === DeliveryType.RECEIVE ||
  order.delivery.type === DeliveryType.PICKUP
